import { useEffect, useState } from 'react';
import { MenuItem, Select, SelectChangeEvent } from '@mui/material';

import AuditLogsModal from './AuditLogsModal';
import UpdateStepStatusModal from './UpdateStepStatusModal';

import Card from '@/components/Card';
import Stack from '@/components/Stack';
import { Body, Small } from '@/components/Typography';
import {
  OnboardingStepsAPI,
  OnboardingStep,
} from '@/services/onboardingStepsAPI';

type Props = {
  workerId: number;
};

const stepStatusColor: Record<string, string> = {
  pending: '#6B7280',
  in_progress: '#F59E0B',
  completed: '#10B981',
  under_review: '#3B82F6',
  rejected: '#EF4444',
  failed: '#EF4444',
  skipped: '#9CA3AF',
};

const stepStatusLabel: Record<string, string> = {
  pending: 'Pending',
  in_progress: 'In Progress',
  completed: 'Completed',
  under_review: 'Under Review',
  rejected: 'Rejected',
  failed: 'Failed',
  skipped: 'Skipped',
};

// Steps that cannot be manually bypassed - must be completed by the worker
const PROTECTED_STEPS = [
  'employee_handbook',
  'employee_information',
  'ssn_information',
  'tax_forms',
  'payment_method',
];

const OnboardingStepsCard = ({ workerId }: Props) => {
  const [steps, setSteps] = useState<OnboardingStep[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [updating, setUpdating] = useState<string | null>(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [auditLogsModalOpen, setAuditLogsModalOpen] = useState(false);
  const [pendingStatusChange, setPendingStatusChange] = useState<{
    stepId: string;
    stepTitle: string;
    currentStatus: string;
    newStatus: string;
  } | null>(null);

  const fetchOnboardingSteps = async () => {
    try {
      setLoading(true);
      const response =
        await OnboardingStepsAPI.getWorkerOnboardingSteps(workerId);
      if (response.success && response.steps) {
        setSteps(response.steps);
      }
    } catch (err) {
      setError('Failed to load onboarding steps');
      console.error('Error fetching onboarding steps:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    // Always fetch from API to get the full step data with disabled flags
    fetchOnboardingSteps();
  }, [workerId]);

  const handleStatusChangeClick = (
    stepId: string,
    stepTitle: string,
    currentStatus: string,
    newStatus: string,
  ) => {
    setPendingStatusChange({ stepId, stepTitle, currentStatus, newStatus });
    setModalOpen(true);
  };

  const handleConfirmStatusChange = async (note: string) => {
    if (!pendingStatusChange) return;

    try {
      setUpdating(pendingStatusChange.stepId);
      const response = await OnboardingStepsAPI.updateWorkerOnboardingSteps(
        workerId,
        pendingStatusChange.stepId,
        pendingStatusChange.newStatus,
        note,
      );

      if (response.success && response.steps) {
        setSteps(response.steps);
        setModalOpen(false);
        setPendingStatusChange(null);
        // Force refresh the page after successful update
        window.location.reload();
      }
    } catch (err) {
      console.error('Error updating step status:', err);
      setError('Failed to update step status');
      setModalOpen(false);
      setPendingStatusChange(null);
      setUpdating(null);
    }
  };

  const handleCancelStatusChange = () => {
    setModalOpen(false);
    setPendingStatusChange(null);
  };

  if (loading) {
    return (
      <Card title="Onboarding Steps">
        <Card.Section>
          <Body>Loading...</Body>
        </Card.Section>
      </Card>
    );
  }

  if (error) {
    return (
      <Card title="Onboarding Steps">
        <Card.Section>
          <Body color="lighter">{error}</Body>
        </Card.Section>
      </Card>
    );
  }

  if (!steps || steps.length === 0) {
    return (
      <Card title="Onboarding Steps">
        <Card.Section>
          <Body color="lighter">No onboarding steps data available</Body>
        </Card.Section>
      </Card>
    );
  }

  const completedSteps = steps.filter((s) => s.status === 'completed').length;
  const totalSteps = steps.length;
  const completionPercentage =
    totalSteps > 0 ? Math.round((completedSteps / totalSteps) * 100) : 0;

  // Helper function to check if a step can be manually updated
  const isStepProtected = (stepId: string) => PROTECTED_STEPS.includes(stepId);

  return (
    <Card
      actions={[
        {
          label: 'View Audit Logs',
          onAction: () => setAuditLogsModalOpen(true),
        },
      ]}
      title="Onboarding Steps"
    >
      <Card.Section>
        <Stack vertical gap={8}>
          <Stack style={{ justifyContent: 'space-between' }}>
            <Body weight="semibold">Progress</Body>
            <Body weight="semibold">
              {completedSteps} / {totalSteps}
            </Body>
          </Stack>
          <div
            style={{
              width: '100%',
              height: '8px',
              backgroundColor: '#E5E7EB',
              borderRadius: '4px',
              overflow: 'hidden',
            }}
          >
            <div
              style={{
                width: `${completionPercentage}%`,
                height: '100%',
                backgroundColor: '#10B981',
                transition: 'width 0.3s ease',
              }}
            />
          </div>
          <Small color="lighter">{completionPercentage}% Complete</Small>
        </Stack>
      </Card.Section>
      <Card.Section>
        <Stack vertical gap={12}>
          {steps.map((step) => {
            const isProtected = isStepProtected(step.id);
            const isDisabled =
              step.disabled || updating === step.id || isProtected;

            return (
              <Stack
                key={step.id}
                vertical
                gap={8}
                style={{
                  padding: '12px',
                  backgroundColor: step.disabled ? '#F3F4F6' : '#F9FAFB',
                  borderRadius: '8px',
                  borderLeft: `4px solid ${stepStatusColor[step.status] || '#6B7280'}`,
                  opacity: step.disabled ? 0.6 : 1,
                }}
              >
                <Stack
                  style={{
                    justifyContent: 'space-between',
                    alignItems: 'flex-start',
                    gap: '8px',
                  }}
                >
                  <Body weight="semibold">{step.title}</Body>
                  <Select
                    disabled={isDisabled}
                    size="small"
                    sx={{
                      minWidth: '140px',
                      fontSize: '12px',
                      fontWeight: 600,
                      backgroundColor:
                        (stepStatusColor[step.status] || '#6B7280') + '20',
                      color: stepStatusColor[step.status] || '#6B7280',
                      '& .MuiOutlinedInput-notchedOutline': {
                        borderColor:
                          (stepStatusColor[step.status] || '#6B7280') + '40',
                      },
                      '&:hover .MuiOutlinedInput-notchedOutline': {
                        borderColor: stepStatusColor[step.status] || '#6B7280',
                      },
                      '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                        borderColor: stepStatusColor[step.status] || '#6B7280',
                      },
                    }}
                    value={step.status}
                    onChange={(e: SelectChangeEvent) =>
                      handleStatusChangeClick(
                        step.id,
                        step.title,
                        step.status,
                        e.target.value,
                      )
                    }
                  >
                    <MenuItem value="pending">
                      <span style={{ color: stepStatusColor.pending }}>
                        {stepStatusLabel.pending}
                      </span>
                    </MenuItem>
                    <MenuItem value="in_progress">
                      <span style={{ color: stepStatusColor.in_progress }}>
                        {stepStatusLabel.in_progress}
                      </span>
                    </MenuItem>
                    <MenuItem value="completed">
                      <span style={{ color: stepStatusColor.completed }}>
                        {stepStatusLabel.completed}
                      </span>
                    </MenuItem>
                    <MenuItem value="under_review">
                      <span style={{ color: stepStatusColor.under_review }}>
                        {stepStatusLabel.under_review}
                      </span>
                    </MenuItem>
                    <MenuItem value="rejected">
                      <span style={{ color: stepStatusColor.rejected }}>
                        {stepStatusLabel.rejected}
                      </span>
                    </MenuItem>
                  </Select>
                </Stack>
                {step.disabled && (
                  <Small color="lighter">
                    This step is locked until previous steps are completed
                  </Small>
                )}
                {isProtected && !step.disabled && (
                  <Small color="lighter">
                    This step cannot be bypassed - must be completed by the
                    worker
                  </Small>
                )}
              </Stack>
            );
          })}
        </Stack>
      </Card.Section>
      {pendingStatusChange && (
        <UpdateStepStatusModal
          currentStatus={pendingStatusChange.currentStatus}
          newStatus={pendingStatusChange.newStatus}
          open={modalOpen}
          stepTitle={pendingStatusChange.stepTitle}
          onCancel={handleCancelStatusChange}
          onConfirm={handleConfirmStatusChange}
        />
      )}
      <AuditLogsModal
        open={auditLogsModalOpen}
        workerId={workerId}
        onClose={() => setAuditLogsModalOpen(false)}
      />
    </Card>
  );
};

export default OnboardingStepsCard;
